import { useState, useEffect, useRef } from "react";
import { Search, X, Calendar, FileText } from "lucide-react";
import { API } from "@/utils/api";
import { MOOD_EMOJIS } from "@/utils/constants";
import DOMPurify from "dompurify";

const SearchBar = ({ onSelectEntry }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/entries/search?q=${encodeURIComponent(query)}`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setResults(data);
          setIsOpen(true);
        }
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleSelectEntry = (entry) => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    onSelectEntry(entry);
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
  };

  // Helper to strip HTML tags for preview (sanitized)
  const stripHtml = (html) => {
    const sanitized = DOMPurify.sanitize(html || '');
    const tmp = document.createElement("div");
    tmp.innerHTML = sanitized;
    return tmp.textContent || tmp.innerText || "";
  };

  // Helper to highlight matching text
  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={`match-${i}-${part}`} className="rounded px-0.5" style={{ backgroundColor: 'rgba(var(--theme-secondary-rgb), 0.4)', color: 'var(--theme-text)' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div ref={searchRef} className="relative w-full" data-testid="search-bar">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-primary)]" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your journal entries..."
          className="w-full pl-10 pr-10 py-3 rounded-xl focus:outline-none transition-all placeholder-themed"
          style={{
            backgroundColor: 'var(--theme-background-secondary)',
            border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)',
            color: 'var(--theme-text)',
            '--placeholder-color': 'var(--theme-text-secondary)'
          }}
          data-testid="search-input"
        />
        <style>{`
          .placeholder-themed::placeholder {
            color: var(--placeholder-color, var(--theme-text-secondary));
            opacity: 0.7;
          }
        `}</style>
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[var(--theme-background-secondary)] rounded-full transition-colors"
          >
            <X className="w-4 h-4" style={{ color: 'var(--theme-text-secondary)' }} />
          </button>
        )}
        {loading && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2">
            <div 
              className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'rgba(var(--theme-primary-rgb), 0.3)', borderTopColor: 'var(--theme-primary)' }}
            />
          </div>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 rounded-xl shadow-xl shadow-black/50 max-h-80 overflow-y-auto z-50"
          style={{
            backgroundColor: 'var(--theme-background)',
            border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
          }}
        >
          <div className="p-2">
            <p className="text-xs px-2 py-1" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>
              Found {results.length} {results.length === 1 ? "entry" : "entries"}
            </p>
            {results.map((entry) => {
              const moodData = MOOD_EMOJIS.find((m) => m.value === entry.mood);
              const contentPreview = stripHtml(entry.content).slice(0, 100);

              return (
                <button
                  key={entry.id}
                  onClick={() => handleSelectEntry(entry)}
                  className="w-full p-3 text-left hover:bg-[var(--theme-background-secondary)] rounded-lg transition-colors group"
                  data-testid={`search-result-${entry.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{moodData?.emoji || "✨"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {entry.title ? (
                          <h4 className="font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
                            {highlightMatch(entry.title, query)}
                          </h4>
                        ) : (
                          <h4 className="font-semibold italic" style={{ color: 'var(--theme-text-secondary)' }}>Untitled</h4>
                        )}
                      </div>
                      <p className="text-sm line-clamp-2" style={{ color: 'var(--theme-text-secondary)' }}>
                        {highlightMatch(contentPreview, query)}...
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Calendar className="w-3 h-3 text-[var(--theme-secondary)]" />
                        <span className="text-xs" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>{entry.date}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* No Results Message */}
      {isOpen && query.length >= 2 && results.length === 0 && !loading && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 rounded-xl shadow-xl shadow-black/50 p-6 text-center z-50"
          style={{
            backgroundColor: 'var(--theme-background)',
            border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
          }}
        >
          <FileText className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--theme-text-secondary)', opacity: 0.4 }} />
          <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>No entries found for "{query}"</p>
          <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }}>Try a different search term</p>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
